import React, { useContext, useEffect, useState } from 'react'

import {
  Box, Columns, DashboardModule, Form, Grid, InputSearch, LoadingModule, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import api, { baseURL } from '@functions/api'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CanvaTemplatePage from './components/CanvaTemplatePage'

const MODAL_KEY = 'CanvaTemplateSelectionModal'

type ModalCallbacks = {
  approveCampaignQuote: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

type CanvaTemplate = {
  continuation?: string,
  create_url: string,
  created_at: number,
  id: string,
  thumbnail: {
    width: number,
    height: number,
    url: string,
  },
  title: string,
  updated_at: number,
  view_url: string,
}

const TEMPLATE_PAGE_LIMIT = 25

const CanvaTemplateSelectionModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const [loading, setLoading] = useState(false)
  const [continuation, setContinuation] = useState<string | null>(null)
  const [nextContinuation, setNextContinuation] = useState<string | null>(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [templates, setTemplates] = useState<CanvaTemplate[]>([])

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext
  const modalPayload = modalData?.[MODAL_KEY] || {}

  const { agency } = modalPayload
  const agencyId = agency?.id

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query)
    }, 350)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [query])

  useEffect(() => {
    setContinuation(null)
    setNextContinuation(null)
  }, [agencyId, debouncedQuery])

  useEffect(() => {
    if (!agencyId){
      return
    }

    const fetchBrandTemplates = async () => {
      try {
        setLoading(true)

        const normalizedQuery = debouncedQuery.trim().toLowerCase()

        const requestOptions: Record<string, unknown> = normalizedQuery
          ? { agency_id: agencyId, limit: TEMPLATE_PAGE_LIMIT, query: normalizedQuery }
          : { agency_id: agencyId, limit: TEMPLATE_PAGE_LIMIT }

        if (continuation) {
          requestOptions.continuation = continuation
        }

        const { data } = await api(`${baseURL}/canva/get_brand_templates`, requestOptions)
        let templatesFromResponse: CanvaTemplate[] = []

        if (Array.isArray(data)){
          templatesFromResponse = data
        } else if (Array.isArray((data as { templates?: CanvaTemplate[] })?.templates)){
          templatesFromResponse = (data as { templates?: CanvaTemplate[] }).templates || []
        }

        const continuationFromResponse = templatesFromResponse[0]?.continuation || null

        setNextContinuation(continuationFromResponse)
        setCanLoadMore(!!continuationFromResponse)

        setTemplates((previousTemplates) => {
          if (!continuation) {
            return templatesFromResponse
          }

          return [...previousTemplates, ...templatesFromResponse]
        })
      } catch {
        // Ignore for now until template selection UI is wired.
        if (!continuation) {
          setTemplates([])
        }
        setNextContinuation(null)
        setCanLoadMore(false)
      } finally {
        setLoading(false)
      }
    }

    fetchBrandTemplates()
  }, [agencyId, continuation, debouncedQuery])

  const loadMore = () => {
    if (loading || !canLoadMore) {
      return
    }

    setContinuation(nextContinuation)
  }

  const initialLoading = loading && !continuation
  const hasTemplates = templates.length > 0

  return (
    <SidebarModal
      callbacks={callbacks}
      disableAnimation
      disableOverlay
      modalKey={MODAL_KEY}
      showModal={showModal}
      size="fullscreen"
    >
      <SidebarModal.Header callbacks={callbacks} title="Select" titleSecondLine="Template" />

      <SidebarModal.Content boxProps={{ backgroundColor: 'backgroundColor' }} hasFooter={false}>
        <Columns flexDirection={['column-reverse', 'column-reverse', 'row']}>
          <Columns.Main>

            <Form.Field
              direction="column"
              label="Filter by Title"
            >
              {/* @ts-expect-error TS2741 */}
              <InputSearch
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                value={query}
              />
            </Form.Field>

            <LoadingModule loading={initialLoading} times={3} boxProps={{marginTop: 'large'}}/>

            {!initialLoading && (
              <>
              <DashboardModule title="Templates" boxProps={{ marginTop: 'large', marginBottom: 'none' }}>

                {templates.length === 0 && !loading && (
                  <DashboardModule.BlankState>
                    <DashboardModule.BlankState.Title>
                      No Templates Present.
                    </DashboardModule.BlankState.Title>
                  </DashboardModule.BlankState>
                )}

                {templates.length > 0 && (
                  <Box flexDirection="column">
                    <Grid
                      // @ts-expect-error TS2322
                      gridGap="large"
                      gridAutoRows="1fr"
                      gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))"
                      gridTemplateRows="repeat(auto-fill, minmax(240px, 1fr))"
                      padding="large"
                    >
                      {templates.map(template => (
                        <CanvaTemplatePage
                          key={template.id}
                          callbacks={{
                            selectTemplate: (selectedTemplate) => {
                              window.open(selectedTemplate.create_url, '_blank', 'noopener,noreferrer')
                              closeModal()
                            },
                          }}
                          template={template}
                        />
                      ))}
                    </Grid>
                  </Box>
                )}
              </DashboardModule>

              {!initialLoading && hasTemplates && (
                <Box variant="white">
                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={1}
                    loading={loading}
                    showBorderTop={false}
                  />
                </Box>
              )}
              </>
            )}
          </Columns.Main>
        </Columns>
      </SidebarModal.Content>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CanvaTemplateSelectionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
